"use client";

import { Groups, WaterfallChart } from "@mui/icons-material";
import { CompanyType } from "@prisma/client";
import { Flag, LayoutDashboard } from "lucide-react";
import { useRouter } from "next/router";
import React from "react";
import { Link } from "~/components/ui/core/Link";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "~/components/ui/shadcn/ui/sidebar";
import { usePermissions } from "~/hooks/usePermissions";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";

export const SidebarYourData: React.FC = () => {
  const { t } = useI18n();
  const { user } = useSession();
  const { permissions } = usePermissions();
  const router = useRouter();

  if (!user) return null;

  const showPeopleDashboard = true;
  const showSalaryBands = permissions.canAccessSalaryBands;
  const showCompanyDashboard = permissions.canAccessCompanyDashboard;
  const showGenderPayGapReport = permissions.canAccessGenderPayGapReport;
  const showVcDashboard = user.company.type === CompanyType.VENTURE_CAPITAL;

  const showSection =
    showPeopleDashboard || showSalaryBands || showCompanyDashboard || showVcDashboard || showGenderPayGapReport;

  if (!showSection) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t("components.layout.sidebar.your-data")}</SidebarGroupLabel>
      <SidebarMenu>
        {showPeopleDashboard && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.people")}
              isActive={router.pathname === "/people"}
            >
              <Link to={{ pathname: "/people" }} noLinkStyle>
                <Groups />
                <span>{t("components.layout.sidebar.people")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {showSalaryBands && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.salary-bands")}
              isActive={router.pathname.startsWith("/salary-bands")}
            >
              <Link to={{ pathname: "/salary-bands" }} noLinkStyle>
                <WaterfallChart className="rotate-90" />
                <span>{t("components.layout.sidebar.salary-bands")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {showCompanyDashboard && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.company-dashboard")}
              isActive={router.pathname.startsWith("/company-dashboard")}
            >
              <Link to={{ pathname: "/company-dashboard" }} noLinkStyle>
                <LayoutDashboard />
                <span>{t("components.layout.sidebar.company-dashboard")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {showVcDashboard && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.vc-dashboard")}
              isActive={router.pathname.startsWith("/vc-dashboard")}
            >
              <Link to={{ pathname: "/vc-dashboard" }} noLinkStyle>
                <LayoutDashboard />
                <span>{t("components.layout.sidebar.vc-dashboard")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {showGenderPayGapReport && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.gender-pay-gap-report")}
              isActive={router.pathname.startsWith("/analytics/gender-pay-gap-report")}
            >
              <Link to={{ pathname: "/analytics/gender-pay-gap-report" }} noLinkStyle>
                <Flag />
                <span>{t("components.layout.sidebar.gender-pay-gap-report")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
};
